import Button from "../../components/Buttons/Button";
import React, { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Column, Line } from "../../style";
import { background, secondary } from "../../theme";
import { useApi } from "../../hooks/api";
import { useForm } from "react-hook-form";
import { useGlobal } from "../../hooks/global";
import { H4 } from "../../components/HeaderList/style";
import { maskFunctions } from "../../services/maskServices";
import { DashboardPage } from "../../components/DashboardPage";
import { ContainerButtons, ContainerForm, Form } from "./style";
import { ModalLoading } from "../../components/Loadings/ModalLoading";
import { enabledDisabledOptions } from "./options/enabledDisabledOptions";
import { ContentOrLoading } from "../../components/Loadings/ContentOrLoading";
import { IGenericFormRefProps } from "../../components/GenericForm";
import { fieldTypes, IFieldProps, IGroupProps, MaskTypes } from "../../interfaces";
import { AppConstants } from "../../constants";
import DEFAULT_LOGO from '../../assets/web-check-in-default-logo.png';
import { DrawerVariables } from "../../components/Drawer/DrawerVariables";
import { useDisclosure } from "@mantine/hooks";
import { useWallet } from "../../hooks/wallet";
import { BlockForm } from "../../components/BlockForm";
import { MdNotificationsActive } from "react-icons/md";
import { IoIosColorPalette } from "react-icons/io";
import { FaCalendarCheck } from "react-icons/fa";
import CopyCheckinLink from "../../components/CopyCheckinLink";

interface IConstantsMapper {
  label: string,
  description: string,
  type?: MaskTypes,
  inputType?: fieldTypes,
  required?: boolean,
  options?: {
    label: string;
    value: number | string;
  }[],
  maxChars?: number;
  disabled?: boolean;
}

interface TimezoneOption {
  value: string;
  label: string;
}

const timezoneOptions: TimezoneOption[] = [
  { value: '-720', label: 'UTC-12:00 (Linha Internacional de Data)' },
  { value: '-600', label: 'UTC-10:00 (Honolulu)' },
  { value: '-480', label: 'UTC-08:00 (Los Angeles)' },
  { value: '-420', label: 'UTC-07:00 (Denver)' },
  { value: '-360', label: 'UTC-06:00 (Chicago)' },
  { value: '-300', label: 'UTC-05:00 (Nova York)' },
  { value: '-240', label: 'UTC-04:00 (Manaus)' },
  { value: '-180', label: 'UTC-03:00 (São Paulo, Brasília)' },
  { value: '-120', label: 'UTC-02:00 (Fernando de Noronha)' },
  { value: '-60', label: 'UTC-01:00 (Açores)' },
  { value: '0', label: 'UTC±00:00 (Londres)' },
  { value: '60', label: 'UTC+01:00 (Paris)' },
  { value: '120', label: 'UTC+02:00 (Cairo)' },
  { value: '180', label: 'UTC+03:00 (Moscou)' },
  { value: '210', label: 'UTC+03:30 (Teerã)' },
  { value: '240', label: 'UTC+04:00 (Dubai)' },
  { value: '300', label: 'UTC+05:00 (Paquistão)' },
  { value: '360', label: 'UTC+06:00 (Almaty)' },
  { value: '480', label: 'UTC+08:00 (Pequim, Hong Kong)' },
  { value: '540', label: 'UTC+09:00 (Tóquio)' },
  { value: '600', label: 'UTC+10:00 (Sydney)' },
  { value: '660', label: 'UTC+11:00 (Austrália: Ilha Norfolk)' },
  { value: '720', label: 'UTC+12:00 (Ilhas Fiji)' },
];


export const GeneralSettings: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [loadingSave, setLoadingSave] = useState(false);

  const { api, user } = useApi();
  const { subscription } = useWallet();
  const { notify, theme, business } = useGlobal();

  const form_ref = useRef<IGenericFormRefProps>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [opened, { open, close }] = useDisclosure(false);

  const isHaveCustonWebCheckinPermission = subscription?.plans?.permissions.includes("CUSTOMIZABLE_WEB_CHECKIN");

  const CONSTANTS_MAPPER: { [key: string]: IConstantsMapper } = {
    ENABLED_NOTIFICATION_PENDING_CHECK_IN: {
      label: "Notificação de check-ins pendente",
      description: "Habilitando esta notificação o sistema enviará automaticamente para todos os administradores um relatório por e-mail de checkins pendentes",
      type: 'onlyNumber',
      inputType: 'switch',
      options: enabledDisabledOptions
    },
    ENABLED_NOTIFICATION_DISCONNECTED_WHATSAPP: {
      label: "Notificação de WhatsApp desconectado",
      description: "Habilitando esta notificação o sistema enviará automaticamente para todos os administradores uma notificação caso o WhatsApp esteja desconectado",
      type: 'onlyNumber',
      inputType: 'switch',
      options: enabledDisabledOptions
    },
    DAYS_TO_ABLE_CHECK_IN: {
      label: "Dias para habilitar check-in",
      description: "Defina com quantos dias de antecedência o hospede poderá realizar o seu check-in",
      type: 'onlyNumber'
    },
    DAYS_TO_RESEND_NOTIFICATION: {
      label: "Dias para enviar notificações em atraso",
      description: "Caso a notificação não tenha sido enviada na data correta, após quantos dias de atraso ela ainda poderá ser enviada pelo sistema",
      type: 'onlyNumber'
    },
    ENABLED_FACE_MATCH_CHECK_IN_USER: {
      label: "Habilita face-match web check-in",
      description: "Defina se o sistema fará a validação biométrica na documentação dos hóspedes no módulo de web check-in do usuário",
      type: 'onlyNumber',
      inputType: 'switch',
      options: enabledDisabledOptions
    },
    ENABLED_FACE_MATCH_CHECK_IN_ADM: {
      label: "Habilita face-match administração",
      description: "Defina se o sistema fará a validação biométrica na documentação dos hóspedes no módulo de web check-in da administração",
      type: 'onlyNumber',
      inputType: 'switch',
      options: enabledDisabledOptions
    },
    ENABLED_CHECK_ONLY_STATUS_RESERVED: {
      label: "Habilitar check-in por status",
      description: `Com essa opção habilitada só será possível realizar check-ins de reservas com o status "Reservado"`,
      type: 'onlyNumber',
      inputType: 'switch',
      options: enabledDisabledOptions
    },
    ENABLED_CHECKIN_VIEW_PASSWORD: {
      label: "Habilita visualização de senha no web check-in",
      description: "Defina se o sistema mostrara a senha da acomodação quando o usuário concluir o web check-in",
      type: 'onlyNumber',
      inputType: 'switch',
      options: enabledDisabledOptions
    },
    TIMEZONE: {
      label: "Timezone",
      description: "Fuso horário padrão para horário de envio de notificações",
      type: 'text',
      inputType: 'select-fixed',
      options: timezoneOptions
    },
    COMPANY_LOGO: {
      label: "Sua logo",
      description: isHaveCustonWebCheckinPermission ? "Personalize o seu web check-in com a logo de sua empresa" : "Seu plano não permite personalizar o check-in",
      inputType: 'file-image',
      required: false,
      disabled: !isHaveCustonWebCheckinPermission
    },
    COMPANY_COLOR: {
      label: "Cor padrão do web check-in",
      description: isHaveCustonWebCheckinPermission ? "Defina a cor que será utilizada como padrão nos elementos do web check-in" : "Seu plano não permite personalizar o check-in",
      type: 'text',
      inputType: 'color',
      required: false,
      disabled: !isHaveCustonWebCheckinPermission
    },
    CHECKIN_TEXT_FISIC_KEY: {
      label: "Mensagem de sucesso sem exibição de senha",
      description: "Mensagem de sucesso do web checkin para chave comum ou quando a exibição da senha estiver desabilitada",
      inputType: 'textarea',
      maxChars: 700
    },
    CHECKIN_TEXT_PASSWORD_KEY: {
      label: "Mensagem de sucesso com exibição de senha",
      description: "Mensagem de sucesso do web checkin quando a senha for exibida, seja ela online ou offline",
      inputType: 'textarea',
      maxChars: 700
    },
  }

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setError,
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: ({} as any),
    mode: "all",
  });

  const _form = watch();

  const transformDefaultLogoInBase64 = useCallback(async () => {
    try {
      const response = await fetch(DEFAULT_LOGO);
      const blob = await response.blob();

      const file = new File([blob], "web-check-in-default-logo.png", { type: blob.type });

      const formData = new FormData();
      formData.append("photo", file);

      const { data } = await api.post("/photos/transform", formData);

      return data.complete
    } catch (err) {
      console.error(err);
    }
  }, []);

  const searchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/constants");

      for (const key of Object.keys(data)) {
        const { type, inputType } = CONSTANTS_MAPPER?.[key] ?? {}

        if (!type && !inputType) continue

        let value = data[key]

        if (type === 'porcen') value = maskFunctions.porcen.mask(value * 100) + ' %'

        if (inputType === 'select-fixed') {
          if (type === 'text') {
            value = timezoneOptions.find((option) => option.value === value);
          } else {
            value = enabledDisabledOptions.find((option) => option.value === value);
          }
        }

        setValue(key, value)
      }
    } catch (error) {
      console.error(error);
      notify("Erro ao buscar as configurações", 'error');
    }
    setLoading(false);
  }, [api]);

  const onSubmit = useCallback(async () => {
    setLoadingSave(true);

    try {
      const form = form_ref.current?.getForm();

      for (const key of Object.keys(form ?? {})) {
        const { type } = CONSTANTS_MAPPER?.[key] ?? {}
        if (!type) continue
        if (type === 'text') {
          form[key] = form[key]?.trim()?.toLowerCase();
          continue;
        }
        let value = Number(form[key])
        if (type === 'porcen') value = value / 100
        form[key] = value
      }

      const logo = form['COMPANY_LOGO'] ?? await transformDefaultLogoInBase64();
      const color = form['COMPANY_COLOR'] ?? AppConstants.DEFAULT_COLOR;

      form['COMPANY_LOGO'] = logo;
      form['COMPANY_COLOR'] = color;

      await api.put("/general-constants", form);

      notify('Configurações atualizadas com sucesso!', 'success');

    } catch (err: any) {
      console.error(err);
      const error = err.response ? err.response?.data : "SERVER ERROR";
      if (error.field)
        setError(error.field, { type: "manual", message: error.detail });
      else notify("ERRO INTERNO DO SISTEMA", "error");
    }

    setLoadingSave(false);
  }, [form_ref, _form, api, searchSettings]);

  const handleStopPropagation = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e?.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit, form_ref, _form, api]
  );

  const groups: IGroupProps[] = [
    {
      name: "",
      label: "Notificações",
      description: "Gerencie as notificações do sistema",
      icon: MdNotificationsActive,
      fields: [
        Object.entries(CONSTANTS_MAPPER)
          .filter(([key]) => ['DAYS_TO_RESEND_NOTIFICATION', 'ENABLED_NOTIFICATION_PENDING_CHECK_IN', 'ENABLED_NOTIFICATION_DISCONNECTED_WHATSAPP'].includes(key))
          .map(([key, { label, description, type, inputType, options, required = true, maxChars, disabled }]) => {
            return {
              name: key,
              label,
              type: inputType || "input",
              mask: type,
              required: required,
              tooltip: description,
              options: options,
              getIsDisabled: (form) => disabled,
              maxChars
            } as IFieldProps
          }),
      ],
    },
    {
      name: "",
      label: "Configurações de check-in",
      description: "Gerencie regras e prazos para seu check-in",
      icon: FaCalendarCheck,
      iconSize: 20,
      fields: [
        Object.entries(CONSTANTS_MAPPER)
          .filter(([key]) =>
            ['DAYS_TO_ABLE_CHECK_IN', 'ENABLED_FACE_MATCH_CHECK_IN_USER',
              'ENABLED_FACE_MATCH_CHECK_IN_ADM', 'ENABLED_CHECK_ONLY_STATUS_RESERVED', 'ENABLED_CHECKIN_VIEW_PASSWORD', 'TIMEZONE'
            ].includes(key))
          .map(([key, { label, description, type, inputType, options, required = true, maxChars, disabled }]) => {
            return {
              name: key,
              label,
              type: inputType || "input",
              mask: type,
              required: required,
              tooltip: description,
              options: options,
              getIsDisabled: (form) => disabled,
              maxChars
            } as IFieldProps
          }),
      ],
    },
    {
      name: "",
      label: "Aparência",
      description: 'Personalize seu web check-in com sua logo e paleta de cores',
      icon: IoIosColorPalette,
      iconSize: 28,
      fields: [
        Object.entries(CONSTANTS_MAPPER)
          .filter(([key]) => ['COMPANY_COLOR', 'COMPANY_LOGO'].includes(key))
          .map(([key, { label, description, type, inputType, options, required = true, maxChars, disabled }]) => {
            return {
              name: key,
              label,
              type: inputType || "input",
              mask: type,
              required: required,
              tooltip: description,
              options: options,
              getIsDisabled: (form) => disabled,
              maxChars
            } as IFieldProps
          }),

        Object.entries(CONSTANTS_MAPPER)
          .filter(([key]) => key === 'CHECKIN_TEXT_FISIC_KEY' || key === 'CHECKIN_TEXT_PASSWORD_KEY')
          .map(([key, { label, description, type, inputType, options, required = true, maxChars }]) => ({
            name: key,
            label,
            type: inputType || "input",
            mask: type,
            required: required,
            tooltip: description,
            options: options,
            maxChars
          }) as IFieldProps),
      ],
      customContent: (
        <>
          <CopyCheckinLink label="Meu Web Check-In" link={process.env.REACT_APP_URL + '/checkin?' + 'bus_id=' + business?.id} />
        </>
      )
    },
  ];

  useEffect(() => { searchSettings() }, [])

  return (
    <DashboardPage>
      <Column style={{ marginBottom: 16 }}>
        <Line>
          <H4>Configurações Gerais</H4>
        </Line>
      </Column>
      <ContentOrLoading loading={loading} theme={theme}>

        <Column style={{ flex: 1, paddingTop: 20 }}>
          <ContainerForm open>
            <Form onSubmit={handleStopPropagation}>
              <BlockForm
                ref={form_ref}
                groups={groups}
                _form={_form}
                control={control}
                trigger={trigger}
                errors={errors}
                setValue={setValue}
                getValues={getValues}
                register={register}
                disabled={loadingSave || !user.user_type.permissions.includes('SETTINGS-UPDATE')}
              />
              <button className="display-none" type="submit" ref={submitButtonRef} />
            </Form>
          </ContainerForm>
        </Column>
        {user.user_type.permissions.includes('SETTINGS-UPDATE') && (
          <ContainerButtons style={{ marginBottom: -10, marginTop: 40 }} theme={theme}>
            <Button
              onClick={open}
              background={background[theme]}
              borderColor={secondary[theme]}
              color={secondary[theme]}
              style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
              children={"Variáveis"}
            />
            <Button
              onClick={() => submitButtonRef.current?.click()}
              background={secondary[theme]}
              color={"#FFF"}
              style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
              children={"Salvar"}
            />
          </ContainerButtons>
        )}
        <ModalLoading loading={loadingSave} theme={theme} />
      </ContentOrLoading>

      <DrawerVariables opened={opened} close={close} />
    </DashboardPage>
  );
};
